import { ReactComponent as OutlineInfoCircle16 } from "assets/svg/outline-info-circle-16.svg";
import { useBlockedIntervals } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/BlockedIntervalsContext";
import DatePicker from "components/date-picker/DatePicker";
import FormControlTime from "components/form-control-time/FormControlTime";
import SessionRepeatRuleEditor from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/session-repeat-rule-editor/SessionRepeatRuleEditor";
import RequiredAsterisk from "components/required-asterisk";
import ToggleSwitch from "components/toggle-switch";
import messages from "messages";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormControl,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import postBlockedInterval from "services/scheduler/postBlockedInterval";
import queryBlockedIntervals from "services/scheduler/queryBlockedIntervals";
import {
  closeSidesheet,
  setEditedBlockedIntervalPreview,
  setNewSessionPlaceholder,
} from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import { useCalendarTime } from "../../../../../../../../contexts/CalendarTimeContext";
import styles from "../../AddToCalendar.module.scss";
import isTimeValid from "../../utils/isTimeValid";
import postBlockedIntervalSeries from "services/scheduler/postBlockedIntervalSeries";
import { getBlockedIntervalsByRepeatRules } from "../../utils/getBlockedIntervalsByRepeatRule";

interface AddBlockedTimeTabProps {
  initialIntervalStart?: number;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  currentBlockedInterval?: CalendarBlockedInterval;
  isActive?: boolean;
}

const AMPLITUDE_COMPONENT_NAME = "add-or-edit-block-interval";
const DATE_FORMAT = "MM/dd/yyyy";

const AddOrEditBlockInterval = ({
  initialIntervalStart,
  setIsDirty,
  currentBlockedInterval,
  isActive = true,
}: AddBlockedTimeTabProps) => {
  const dispatch = useDispatch();
  const { currentCalendarTimezone, timeRange, setAnchorTime } =
    useCalendarTime();
  const { forceReload } = useBlockedIntervals();

  const [adminNote, setAdminNote] = useState(
    currentBlockedInterval?.admin_note || "",
  );
  const [repeatRule, setRepeatRule] =
    useState<InviteSeriesInput["repeat_rule"]>();

  const close = useCallback(() => {
    dispatch(closeSidesheet());
  }, [dispatch]);

  const initialIntervalStartMoment = moment.tz(
    currentBlockedInterval?.start || initialIntervalStart,
    currentCalendarTimezone,
  );

  const initialIntervalEndMoment = currentBlockedInterval
    ? moment.tz(currentBlockedInterval.end, currentCalendarTimezone)
    : moment(initialIntervalStartMoment).add(1, "hour");

  const [title, setTitle] = useState(currentBlockedInterval?.busy_note || "");

  const [isAllDay, setIsAllDay] = useState(() => {
    if (!currentBlockedInterval) {
      return false;
    }

    const { start, end } = currentBlockedInterval;
    const startMoment = moment.tz(start, currentCalendarTimezone);
    const endMoment = moment.tz(end, currentCalendarTimezone);

    return (
      startMoment.format("HH:mm") === "00:00" &&
      endMoment.format("HH:mm") === "00:00"
    );
  });

  const [startDate, setStartDate] = useState(
    moment(initialIntervalStartMoment).startOf("day").valueOf(),
  );
  const [startTime, setStartTime] = useState(
    initialIntervalStartMoment.format("HH:mm"),
  );

  const [endDate, setEndDate] = useState(() => {
    if (!isAllDay) {
      return moment(initialIntervalEndMoment).startOf("day").valueOf();
    }

    return moment(initialIntervalEndMoment)
      .startOf("day")
      .subtract(1, "day")
      .valueOf();
  });
  const [endTime, setEndTime] = useState(
    initialIntervalEndMoment.format("HH:mm"),
  );

  const convertDateAndTimeToISOWithTimezone = useCallback(
    (date: number, time: string) => {
      if (!isTimeValid(time) || !date) {
        return "";
      }

      return moment
        .tz(
          moment.tz(date, currentCalendarTimezone).format(`YYYY-MM-DD ${time}`),
          currentCalendarTimezone,
        )
        .toISOString();
    },
    [currentCalendarTimezone],
  );

  const intervalStartISO = useMemo(
    () => convertDateAndTimeToISOWithTimezone(startDate, startTime),
    [startDate, startTime, convertDateAndTimeToISOWithTimezone],
  );

  const intervalEndISO = useMemo(
    () => convertDateAndTimeToISOWithTimezone(endDate, endTime),
    [endDate, endTime, convertDateAndTimeToISOWithTimezone],
  );

  const [error, setError] = useState<any>();

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const interval: BlockedInterval = {
      ...(currentBlockedInterval ?? {}),
      start: intervalStartISO,
      end: intervalEndISO,
      busy_note: title,
      admin_note: adminNote,
    };

    const previewInterval = isAllDay
      ? convertIntervalToFullDay(interval, currentCalendarTimezone)
      : interval;

    if (currentBlockedInterval) {
      dispatch(setEditedBlockedIntervalPreview(previewInterval));
    } else {
      dispatch(
        setNewSessionPlaceholder({
          appointment_start: previewInterval.start,
          appointment_end: previewInterval.end,
        }),
      );
    }
  }, [
    dispatch,
    intervalStartISO,
    intervalEndISO,
    title,
    adminNote,
    isAllDay,
    currentCalendarTimezone,
    currentBlockedInterval,
    isActive,
  ]);

  const apply = useCallback(async () => {
    const interval: BlockedInterval = {
      ...(currentBlockedInterval ?? {}),
      start: intervalStartISO,
      end: intervalEndISO,
      busy_note: title,
      ...(adminNote && { admin_note: adminNote }),
    };

    try {
      const singleBlockInterval = isAllDay
        ? convertIntervalToFullDay(interval, currentCalendarTimezone)
        : interval;

      if (!repeatRule) {
        await postBlockedInterval(singleBlockInterval);
      } else {
        await postBlockedIntervalSeries({
          repeat_rule: repeatRule,
          unavailabilities: !repeatRule
            ? [singleBlockInterval]
            : getBlockedIntervalsByRepeatRules({
                repeatRule,
                template: singleBlockInterval,
                timezone: currentCalendarTimezone,
              }),
        });
      }

      await queryBlockedIntervals(timeRange);
      forceReload();
      close();
    } catch (error) {
      setError(error);
    }
  }, [
    intervalStartISO,
    intervalEndISO,
    title,
    adminNote,
    timeRange,
    close,
    forceReload,
    isAllDay,
    currentCalendarTimezone,
    currentBlockedInterval,
    repeatRule,
  ]);

  const trackClick = (elementName: string) =>
    track(
      PRO_CALENDAR_ANALYTICS_PAGE_NAME,
      [AMPLITUDE_COMPONENT_NAME, elementName],
      "click",
    );

  const processSelectedDate = useCallback(
    (selectedDate: Date, previousDateValue: number) => {
      const diffDays = moment(selectedDate)
        .tz(currentCalendarTimezone)
        .startOf("day")
        .diff(
          moment(previousDateValue).tz(currentCalendarTimezone).startOf("day"),
          "day",
        );

      const nextDateValue = moment(previousDateValue)
        .tz(currentCalendarTimezone)
        .add(diffDays, "day")
        .toDate()
        .valueOf();

      if (nextDateValue !== previousDateValue) {
        setIsDirty(true);
      }
      return nextDateValue;
    },
    [currentCalendarTimezone, setIsDirty],
  );

  const minEndTime = useMemo(
    () =>
      endDate <= startDate
        ? moment(startTime, "HH:mm").add(15, "minute").format("HH:mm")
        : undefined,
    [startTime, endDate, startDate],
  );

  return (
    <>
      <div className={styles["body"]}>
        {error && <Alert variant="danger">{error.toString()}</Alert>}

        <div className={styles["section"]}>
          <div className={styles["section__title"]}>
            {messages.blockDetails}
          </div>
          <div className={styles["row"]}>
            <label className={styles["label"]}>
              <span className={styles["label-text"]}>
                {messages.blockTitle}
              </span>
              <FormControl
                type="text"
                value={title}
                onChange={(event) => {
                  setIsDirty(true);
                  setTitle(event.target.value);
                }}
                placeholder={messages.enterTitleOfTheBlock}
              />
            </label>
          </div>

          <div className={styles["row"]}>
            <label className={styles["label"]}>
              <span className={styles["label-text"]}>
                {messages.startDate}
                <RequiredAsterisk />
              </span>
              <DatePicker
                placeholderText={DATE_FORMAT.toLowerCase()}
                dateFormat={DATE_FORMAT}
                timezone={currentCalendarTimezone}
                selected={!startDate ? undefined : new Date(startDate)}
                onChange={(selectedDate: Date) => {
                  trackClick("start-date-picker");
                  setAnchorTime(selectedDate.valueOf());
                  setStartDate((prev) => {
                    const newStartDate = processSelectedDate(
                      selectedDate,
                      prev,
                    );
                    if (endDate.valueOf() < newStartDate) {
                      setEndDate(newStartDate);
                    }
                    return newStartDate;
                  });
                }}
              />
            </label>

            <label
              className={getClasses(styles["label"], styles["label--time"])}
            >
              <span className={styles["label-text"]}>
                {messages.time}
                <RequiredAsterisk />
              </span>
              <FormControlTime
                className={styles["add-to-calendar__time"]}
                value={isAllDay ? "--:--" : startTime ?? ""}
                onChange={(e) => {
                  trackClick("start-time-selector");
                  const newStartTime = e.target.value;
                  setIsDirty(true);
                  setStartTime(newStartTime);
                  setEndTime((prev) => {
                    if (startDate === endDate && newStartTime >= prev) {
                      return moment(newStartTime, "HH:mm")
                        .add(15, "minute")
                        .format("HH:mm");
                    }
                    return prev;
                  });
                }}
                disabled={isAllDay}
              />
            </label>
          </div>

          <div className={styles["row"]}>
            <label className={styles["label"]}>
              <span className={styles["label-text"]}>
                {messages.endDate}
                <RequiredAsterisk />
              </span>
              <DatePicker
                minDate={startDate ? new Date(startDate) : undefined}
                placeholderText={DATE_FORMAT.toLowerCase()}
                dateFormat={DATE_FORMAT}
                timezone={currentCalendarTimezone}
                selected={!endDate ? undefined : new Date(endDate)}
                onChange={(selectedDate: Date) => {
                  trackClick("end-date-picker");
                  setEndDate((prev) => processSelectedDate(selectedDate, prev));
                }}
              />
            </label>

            <label
              className={getClasses(styles["label"], styles["label--time"])}
            >
              <span className={styles["label-text"]}>
                {messages.time}
                <RequiredAsterisk />
              </span>
              <FormControlTime
                className={styles["add-to-calendar__time"]}
                value={isAllDay ? "--:--" : endTime ?? ""}
                min={minEndTime}
                onChange={(e) => {
                  trackClick("end-time-selector");
                  setIsDirty(true);
                  setEndTime(e.target.value);
                }}
                disabled={isAllDay}
              />
            </label>
          </div>

          <div className={getClasses(styles["row"], styles["row--switch"])}>
            <ToggleSwitch
              label="All Day"
              checked={isAllDay}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setIsAllDay(event.target.checked);
                if (!event.target.checked) {
                  setStartTime((prev) => (prev !== "00:00" ? prev : "10:00"));
                  setEndTime((prev) => (prev !== "00:00" ? prev : "10:30"));
                }
              }}
            />
            {messages.allDay}
          </div>
          {!currentBlockedInterval && (
            <SessionRepeatRuleEditor
              className={styles["repeat-rule"]}
              value={repeatRule}
              onChange={(repeatRule) => {
                trackClick("session-repeat-option");
                setIsDirty(true);
                setRepeatRule(repeatRule);
              }}
              timezone={currentCalendarTimezone}
              intervalStart={initialIntervalStart}
            />
          )}
        </div>

        <div className={styles["section"]}>
          <div className={styles["section__title"]}>
            {messages.adminNote}
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="admin-note-tooltip">
                  An administrative note is any non-clinical note related to the
                  session. For example, if there’s a reminder that your client
                  mentioned they may be late to your next appointment, you may
                  want to mark that as an administrative note. You can access
                  clinical notes by clicking here.
                </Tooltip>
              }
            >
              <OutlineInfoCircle16 className={"blue ml-1"} />
            </OverlayTrigger>
          </div>
          <Form.Control
            as="textarea"
            rows={5}
            placeholder={messages.enterNote}
            value={adminNote}
            onChange={(event) => {
              setAdminNote(event.target.value);
            }}
          />
        </div>
      </div>

      <div className={styles["footer"]}>
        <Button
          className={styles["button"]}
          variant={"primary"}
          disabled={false}
          onClick={() => {
            trackClick("create-button");
            setError(undefined);
            apply();
          }}
        >
          {currentBlockedInterval ? messages.update : messages.add}
        </Button>
      </div>
    </>
  );
};

const convertIntervalToFullDay = (
  interval: BlockedInterval,
  timezone: string,
) => ({
  ...interval,
  start: moment(interval.start).tz(timezone).startOf("day").toISOString(),
  end: moment(interval.end)
    .tz(timezone)
    .startOf("day")
    .add(1, "day")
    .toISOString(),
});

export default AddOrEditBlockInterval;
